import React, { useState, useEffect } from "react";
import {
  Container,
  Row,
  Col,
  Form,
  FormGroup,
  Label,
  Input,
  Button,
  Card,
  CardBody,
  Spinner,
} from "reactstrap";
import { useNavigate, useParams } from "react-router-dom";
import Select from "react-select";
import apiClient from "../../axiosInstance";
import showAlert from "../../components/Common/ShowAlert";
import Breadcrumbs from "../../components/Common/Breadcrumb";

const UpdateInspectVehicle = () => {
  const navigate = useNavigate();
  const { id } = useParams(); // inspection id from route
  const [breadcrumbItems] = useState([
    { title: "Daryeel", link: "/" },
    { title: "Update Vehicle Inspection", link: "#" },
  ]);

  // Form data (same keys as your create page but camelCase)
  const [form, setForm] = useState({
    vehicleId: "",
    inspectorId: "",
    inspectionDate: "",
    status: "",
  });

  // Item form data with original ID for edit
  const [itemForm, setItemForm] = useState({
    inspectionItemId: "",
    result: "Passed",
    originalItemId: null,
  });
  const [items, setItems] = useState([]);

  // Options
  const [vehicleOptions, setVehicleOptions] = useState([]);
  const [inspectorOptions, setInspectorOptions] = useState([]);
  const [inspectionItemOptions, setInspectionItemOptions] = useState([]);

  // UX
  const [loading, setLoading] = useState(true);

  // Helpers
  const onFormChange = (field, value) =>
    setForm((prev) => ({ ...prev, [field]: value }));
  const onItemChange = (field, value) =>
    setItemForm((prev) => ({ ...prev, [field]: value }));

  // Fetch options + existing inspection
  useEffect(() => {
    const fetchAll = async () => {
      try {
        // 1) Options
        const [vehRes, inspRes, itemRes] = await Promise.all([
          apiClient.get("/registrations/vehicle-registrations"),
          apiClient.get("/users/getAllUsers"),
          apiClient.get("/inspections/inspection-items"),
        ]);

        const vehicles = vehRes.data.response || vehRes.data || [];
        setVehicleOptions(
          vehicles
            .filter((v) => v.status === "Active")
            .map((v) => ({ value: v.id, label: v.license_plate }))
        );

        const users = inspRes.data.response || inspRes.data || [];
        setInspectorOptions(
          users.map((u) => ({ value: u.id, label: u.full_name }))
        );

        const itemsOpt = itemRes.data.response || itemRes.data || [];
        setInspectionItemOptions(
          itemsOpt.map((i) => ({ value: i.id, label: i.name }))
        );

        // 2) Inspection details
        const detRes = await apiClient.get(
          `/inspections/vehicle-inspections/${id}`
        );
        const det = detRes.data;
        console.log("Inspection Details:", det.inspector_id);
        // Map API response → form state
        setForm({
          vehicleId: det.vehicle_id || "",
          inspectorId: det.inspector_id || "",
          inspectionDate: det.inspection_date
            ? det.inspection_date.slice(0, 10) // YYYY-MM-DD
            : "",
          status: det.result || "",
        });

        // Map inspection_results → items [{inspectionItemId, result}]
        const mappedItems = Array.isArray(det.inspection_results)
          ? det.inspection_results.map((r) => ({
              inspectionItemId:
                r.inspection_item_id ?? r.inspectionItemId ?? r.item_id,
              result: r.result,
            }))
          : [];
        setItems(mappedItems);
      } catch (err) {
        console.error(err);
        await showAlert({
          title: "Load Inspection",
          text: "Failed to load inspection details.",
          icon: "error",
        });
        navigate(-1);
      } finally {
        setLoading(false);
      }
    };

    fetchAll();
  }, [id, navigate]);

  // Items table operations (same behavior as your create page)
  const addItem = () => {
    const { inspectionItemId, result, originalItemId } = itemForm;
    if (inspectionItemId && result) {
      if (originalItemId != null) {
        // update existing item
        setItems((prev) =>
          prev.map((it) =>
            it.inspectionItemId === originalItemId
              ? { inspectionItemId, result }
              : it
          )
        );
      } else {
        // add new item if not duplicate
        const exists = items.find(
          (it) => it.inspectionItemId === inspectionItemId
        );
        if (!exists)
          setItems((prev) => [...prev, { inspectionItemId, result }]);
      }
      // reset itemForm
      setItemForm({
        inspectionItemId: "",
        result: "Passed",
        originalItemId: null,
      });
    }
  };

  const removeItem = (inspectionItemId) => {
    setItems((prev) =>
      prev.filter((it) => it.inspectionItemId !== inspectionItemId)
    );
    if (itemForm.originalItemId === inspectionItemId) {
      setItemForm({
        inspectionItemId: "",
        result: "Passed",
        originalItemId: null,
      });
    }
  };

  const editItem = (inspectionItemId) => {
    const it = items.find((x) => x.inspectionItemId === inspectionItemId);
    if (it) {
      setItemForm({
        inspectionItemId: it.inspectionItemId,
        result: it.result,
        originalItemId: it.inspectionItemId,
      });
    }
  };

  const handleUpdate = async () => {
    try {
      const payload = {
        vehicle_id: form.vehicleId,
        inspector_id: form.inspectorId,
        inspection_date: form.inspectionDate,
        status: form.status,
        results: items.map((it) => ({
          inspectionItemId: it.inspectionItemId,
          result: it.result,
        })),
      };

      const res = await apiClient.put(
        `/inspections/vehicle-inspections/${id}`,
        payload
      );

      await showAlert({
        title: "Update Inspection",
        text: res.data.message || "Vehicle inspection updated successfully",
        icon: res.data.status || "success",
      });

      if ((res.data.status || "").toLowerCase() === "success") {
        navigate(-1);
      }
    } catch (err) {
      console.error(err);
      await showAlert({
        title: "Update Inspection",
        text: "Failed to update inspection.",
        icon: "error",
      });
    }
  };

  if (loading) {
    return (
      <div className="page-content">
        <Container
          fluid
          className="d-flex justify-content-center align-items-center"
          style={{ minHeight: 240 }}
        >
          <Spinner />
        </Container>
      </div>
    );
  }

  return (
    <div className="page-content">
      <Container fluid>
        <Breadcrumbs
          title="Update Vehicle Inspection"
          breadcrumbItems={breadcrumbItems}
        />
        <Row>
          <Col md={12}>
            <Card>
              <CardBody>
                <h4 className="card-title d-flex justify-content-between align-items-center">
                  Vehicle Inspection Form
                  <i
                    className="ri-arrow-left-line"
                    style={{ fontSize: "1.25rem", cursor: "pointer" }}
                    onClick={() => navigate(-1)}
                  />
                </h4>
                <hr className="bg-primary" />
                <Form>
                  <Row className="mb-3">
                    <Col md={12}>
                      <Row>
                        <Col md={3}>
                          <FormGroup>
                            <Label>Vehicle</Label>
                            <Select
                              options={[
                                { label: "Select Vehicle", value: "" },
                                ...vehicleOptions,
                              ]}
                              value={
                                vehicleOptions.find(
                                  (o) => o.value === form.vehicleId
                                ) || { value: "", label: "Select Vehicle" }
                              }
                              onChange={(opt) =>
                                onFormChange("vehicleId", opt?.value || "")
                              }
                              styles={{
                                control: (p) => ({
                                  ...p,
                                  backgroundColor: "white",
                                }),
                              }}
                            />
                          </FormGroup>
                        </Col>
                        <Col md={3}>
                          <FormGroup>
                            <Label>Inspector</Label>
                            <Select
                              options={[
                                { label: "Select Inspector", value: "" },
                                ...inspectorOptions,
                              ]}
                              value={
                                inspectorOptions.find(
                                  (o) => o.value === form.inspectorId
                                ) || { value: "", label: "Select Inspector" }
                              }
                              onChange={(opt) =>
                                onFormChange("inspectorId", opt?.value || "")
                              }
                              styles={{
                                control: (p) => ({
                                  ...p,
                                  backgroundColor: "white",
                                }),
                              }}
                            />
                          </FormGroup>
                        </Col>
                        <Col md={3}>
                          <FormGroup>
                            <Label>Inspection Date</Label>
                            <Input
                              type="date"
                              value={form.inspectionDate}
                              onChange={(e) =>
                                onFormChange("inspectionDate", e.target.value)
                              }
                            />
                          </FormGroup>
                        </Col>
                        <Col md={3}>
                          <FormGroup>
                            <Label>Status</Label>
                            <Select
                              options={[
                                { label: "Select Status", value: "" },
                                { value: "Passed", label: "Passed" },
                                { value: "Failed", label: "Failed" },
                              ]}
                              value={
                                form.status
                                  ? { value: form.status, label: form.status }
                                  : { value: "", label: "Select Status" }
                              }
                              onChange={(opt) =>
                                onFormChange("status", opt?.value || "")
                              }
                              styles={{
                                control: (p) => ({
                                  ...p,
                                  backgroundColor: "white",
                                }),
                              }}
                            />
                          </FormGroup>
                        </Col>
                      </Row>

                      <Row className="mb-3 align-items-end">
                        <Col md={6}>
                          <FormGroup>
                            <Label>Inspection Item</Label>
                            <Select
                              options={[
                                { label: "Select Inspection Item", value: "" },
                                ...inspectionItemOptions,
                              ]}
                              value={
                                inspectionItemOptions.find(
                                  (o) => o.value === itemForm.inspectionItemId
                                ) || {
                                  value: "",
                                  label: "Select Inspection Item",
                                }
                              }
                              onChange={(opt) =>
                                onItemChange(
                                  "inspectionItemId",
                                  opt?.value || ""
                                )
                              }
                              styles={{
                                control: (p) => ({
                                  ...p,
                                  backgroundColor: "white",
                                }),
                              }}
                            />
                          </FormGroup>
                        </Col>
                        <Col md={3}>
                          <FormGroup>
                            <Label>Result</Label>
                            <Select
                              options={[
                                { label: "Select Result", value: "" },
                                { value: "Passed", label: "Passed" },
                                { value: "Failed", label: "Failed" },
                              ]}
                              value={
                                itemForm.result
                                  ? {
                                      value: itemForm.result,
                                      label: itemForm.result,
                                    }
                                  : { value: "", label: "Select Result" }
                              }
                              onChange={(opt) =>
                                onItemChange("result", opt?.value || "")
                              }
                              styles={{
                                control: (p) => ({
                                  ...p,
                                  minHeight: "38px",
                                  backgroundColor: "white",
                                }),
                              }}
                            />
                          </FormGroup>
                        </Col>
                        <Col md={3} className="d-flex">
                          <FormGroup className="flex-grow-1 d-flex flex-column justify-content-end">
                            <label className="invisible">Add Button</label>
                            <Button
                              className="flex-grow-1 w-100 align-self-stretch"
                              color="primary"
                              onClick={addItem}
                              disabled={
                                !itemForm.inspectionItemId || !itemForm.result
                              }
                              style={{ height: "38px" }}
                            >
                              {itemForm.originalItemId
                                ? "Update Item"
                                : "Add Item"}
                            </Button>
                          </FormGroup>
                        </Col>
                      </Row>
                    </Col>
                  </Row>

                  <Button
                    color="success"
                    onClick={handleUpdate}
                    disabled={
                      !form.vehicleId ||
                      !form.inspectorId ||
                      !form.inspectionDate ||
                      !form.status ||
                      items.length === 0
                    }
                  >
                    Submit Inspection
                  </Button>
                </Form>
              </CardBody>
            </Card>
          </Col>

          <Col md={12}>
            <Card>
              <CardBody>
                <h4 className="card-title d-flex justify-content-between align-items-center">
                  Vehicle Inspection Form
                </h4>
                <hr className="bg-primary" />
                <table className="table">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Item</th>
                      <th>Result</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((it, idx) => {
                      const opt = inspectionItemOptions.find(
                        (o) => o.value === it.inspectionItemId
                      );
                      return (
                        <tr key={it.inspectionItemId}>
                          <td>{idx + 1}</td>
                          <td>{opt?.label}</td>
                          <td>
                            <span
                              className={`badge bg-${
                                it.result === "Passed" ? "success" : "danger"
                              }`}
                            >
                              {it.result}
                            </span>
                          </td>
                          <td>
                            <Button
                              size="sm"
                              color="warning"
                              onClick={() => editItem(it.inspectionItemId)}
                              className="me-2"
                            >
                              <i className="ri-file-edit-line" />
                            </Button>
                            <Button
                              size="sm"
                              color="danger"
                              onClick={() => removeItem(it.inspectionItemId)}
                            >
                              <i className="ri-delete-bin-line" />
                            </Button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default UpdateInspectVehicle;
